/**
 * ---------------------------------------------------------------------------------------------------------------------
 * USP CAROUSEL
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * The product page's feature icon row. On a phone only two of the three items fit, so the row scrolls and this
 * rotates it, a step at a time, so the third comes into view on its own.
 *
 * It does nothing at all unless the row actually overflows, which means desktop and tablet, where all three are in
 * the row already, and any product with one or two features, are left alone without needing to know the breakpoint. Rotation holds while the row is hovered, being
 * dragged, scrolled off screen or on a hidden tab, and never starts for a visitor who has asked for reduced motion.
 *
 * Standalone rather than part of custom.js: that bundle is built from src/js by CodeKit, and this needs to ship
 * without a rebuild. Loaded from product-usp-icons.liquid, which only renders when a product has features set.
 */
(function () {
  if (window.customElements.get('usp-carousel')) {
    return;
  }

  var INTERACTION_GRACE = 4000;

  class UspCarousel extends HTMLElement {
    connectedCallback() {
      this.interval = (parseFloat(this.getAttribute('interval')) || 4) * 1000;

      this.hovered = false;
      this.interacting = false;
      this.visible = true;
      this.interactionTimeout = null;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onEnter = this.onEnter.bind(this);
      this.onLeave = this.onLeave.bind(this);
      this.onInteract = this.onInteract.bind(this);
      this.tick = this.tick.bind(this);

      this.addEventListener('pointerenter', this.onEnter);
      this.addEventListener('pointerleave', this.onLeave);
      this.addEventListener('focusin', this.onEnter);
      this.addEventListener('focusout', this.onLeave);

      // A drag, a swipe or a wheel over the row hands it to the visitor; the
      // rotation picks up again once they have stopped.
      this.addEventListener('pointerdown', this.onInteract);
      this.addEventListener('touchstart', this.onInteract, { passive: true });
      this.addEventListener('wheel', this.onInteract, { passive: true });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            this.visible = entries[0].isIntersecting;
          },
          { threshold: 0.2 }
        );

        this.observer.observe(this);
      }

      this.timer = window.setInterval(this.tick, this.interval);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
      window.clearTimeout(this.interactionTimeout);

      if (this.observer) {
        this.observer.disconnect();
      }
    }

    onEnter() {
      this.hovered = true;
    }

    onLeave() {
      this.hovered = false;
    }

    onInteract() {
      this.interacting = true;

      window.clearTimeout(this.interactionTimeout);

      this.interactionTimeout = window.setTimeout(() => {
        this.interacting = false;
      }, INTERACTION_GRACE);
    }

    tick() {
      if (this.hovered || this.interacting || !this.visible || document.hidden) {
        return;
      }

      if (this.reducedMotion.matches) {
        return;
      }

      // Everything fits: the desktop grid, or a product with fewer features
      // than the row can show.
      var furthest = this.scrollWidth - this.clientWidth;

      if (furthest < 1) {
        return;
      }

      var first = this.children[0];
      var second = this.children[1];
      var step = second ? second.offsetLeft - first.offsetLeft : this.clientWidth;
      var left;

      // Back to the start rather than stopping on the last item, then a step
      // at a time, with the last one landing on the end however short it
      // falls. Read off where the row is now, not off where the step would
      // reach: with three items and room for two, one step is the whole
      // scroll, and testing the destination would send every step back to the
      // start. The compare allows a pixel, as a smooth scroll can land a
      // fraction short of the end.
      if (this.scrollLeft >= furthest - 1) {
        left = 0;
      } else {
        left = Math.min(this.scrollLeft + step, furthest);
      }

      this.scrollTo({ left: left, behavior: 'smooth' });
    }
  }

  window.customElements.define('usp-carousel', UspCarousel);
})();
